package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.model.CartItem
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.Order
import diana.soleil.design.iraniancanadienbazzar.core.model.OrderStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.PaymentStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Product
import diana.soleil.design.iraniancanadienbazzar.core.model.PropertyDetails
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json

/** Products a business sells. Public read; writes are owner/admin (rules-enforced). */
interface ProductRepository {
    fun observeProducts(businessId: String): Flow<DataResult<List<Product>>>

    /**
     * Owner adds/updates a product ([productId] null = create). Price in integer cents.
     * [property] carries optional real-estate facts; pass the existing value through on updates —
     * the write includes the field, so null clears it.
     */
    suspend fun upsertProduct(
        businessId: String,
        productId: String?,
        name: LocalizedText,
        description: LocalizedText,
        priceCents: Long,
        available: Boolean,
        promoted: Boolean,
        property: PropertyDetails? = null,
    ): DataResult<Unit>
}

/** Orders placed for pickup. Create is buyer-only + forced pending/unpaid (rules). */
interface OrderRepository {
    /** Places an order from the cart lines; returns the new order id. */
    suspend fun placeOrder(items: List<CartItem>, note: String): DataResult<String>

    /** Streams the signed-in user's orders, newest first. */
    fun observeMyOrders(): Flow<DataResult<List<Order>>>

    /** One-shot read (confirmation screen). */
    suspend fun getOrder(orderId: String): DataResult<Order>

    /**
     * Streams the orders placed with [businessId] for the owner's inbox, newest first (rules limit
     * reads to the business owner/admin). This is the counter-side of the pickup flow — without it
     * orders were write-only and silently died in `pending`.
     */
    fun observeBusinessOrders(businessId: String): Flow<DataResult<List<Order>>>

    /**
     * Owner moves an order through the legal machine (rules enforce the transitions:
     * pending→confirmed→fulfilled, cancel from non-terminal, paymentStatus settles any time).
     * Null leaves a field unchanged.
     */
    suspend fun updateOrderStatus(
        orderId: String,
        status: OrderStatus? = null,
        paymentStatus: PaymentStatus? = null,
    ): DataResult<Unit>

    /** Buyer cancels their own order — rules allow it only while the order is still pending. */
    suspend fun cancelMyOrder(orderId: String): DataResult<Unit>
}

/**
 * Device-local cart, persisted via [AppPreferences] (JSON). **Single-business**: orders are per
 * business, so adding an item from a different business replaces the cart (callers warn first via
 * [wouldReplaceCart]). Quantities clamp to 1..99.
 */
class CartController(private val preferences: AppPreferences) {

    private val json = Json { ignoreUnknownKeys = true }
    private val serializer = ListSerializer(CartItem.serializer())

    private val _items = MutableStateFlow(load())
    val items: StateFlow<List<CartItem>> = _items.asStateFlow()

    val activeBusinessId: String? get() = _items.value.firstOrNull()?.businessId

    fun wouldReplaceCart(businessId: String): Boolean =
        _items.value.isNotEmpty() && activeBusinessId != businessId

    fun add(item: CartItem) {
        val current = if (wouldReplaceCart(item.businessId)) emptyList() else _items.value
        val existing = current.firstOrNull { it.productId == item.productId }
        val next = if (existing != null) {
            current.map {
                if (it.productId == item.productId) it.copy(quantity = (it.quantity + item.quantity).coerceIn(1, 99))
                else it
            }
        } else {
            current + item.copy(quantity = item.quantity.coerceIn(1, 99))
        }
        persist(next)
    }

    fun setQuantity(productId: String, quantity: Int) {
        persist(
            if (quantity <= 0) _items.value.filterNot { it.productId == productId }
            else _items.value.map { if (it.productId == productId) it.copy(quantity = quantity.coerceIn(1, 99)) else it },
        )
    }

    fun remove(productId: String) = setQuantity(productId, 0)

    fun clear() = persist(emptyList())

    fun subtotalCents(): Long = _items.value.sumOf { it.lineTotalCents }

    private fun persist(items: List<CartItem>) {
        preferences.cartJson = if (items.isEmpty()) null else json.encodeToString(serializer, items)
        _items.value = items
    }

    private fun load(): List<CartItem> =
        preferences.cartJson?.let { runCatching { json.decodeFromString(serializer, it) }.getOrNull() }.orEmpty()
}
